/**
 * Username validation utilities with Unicode property escape fallback
 * Handles environments where Unicode property escapes may not be supported
 */

/**
 * Tests if Unicode property escapes are supported in the current environment
 * @returns true if Unicode property escapes work, false otherwise
 */
export const isUnicodePropertyEscapeSupported = (): boolean => {
    try {
        // Test Unicode property escape support
        new RegExp('\\p{L}', 'u')
        return true
    } catch {
        return false
    }
}

/**
 * Gets the appropriate username regex based on Unicode support
 * @returns RegExp for username validation
 */
export const getUsernameRegex = (): RegExp => {
    if (isUnicodePropertyEscapeSupported()) {
        // Full Unicode support: letters, numbers, underscore, hyphen
        return /^[\p{L}\p{N}_-]+$/u
    } else {
        // ASCII fallback: letters, numbers, underscore, hyphen
        return /^[A-Za-z0-9_-]+$/
    }
}

export type UsernameValidationResult =
    | {
          ok: true
      }
    | {
          ok: false
          code: 'username.invalid'
          reason: 'chars' | 'length'
      }

/**
 * Validates a username according to the rules
 * @param username - The username to validate
 * @returns Validation result with success status and error details
 */
export const validateUsername = (username: string): UsernameValidationResult => {
    // Check length first (3-20 characters)
    if (!username || username.length < 3) {
        return {
            ok: false,
            code: 'username.invalid',
            reason: 'length',
        }
    }

    if (username.length > 20) {
        return {
            ok: false,
            code: 'username.invalid',
            reason: 'length',
        }
    }

    // Check character validity
    const regex = getUsernameRegex()
    if (!regex.test(username)) {
        return {
            ok: false,
            code: 'username.invalid',
            reason: 'chars',
        }
    }

    return { ok: true }
}
